import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from '../hooks/useTranslation'
import { X, Phone, Clock } from 'lucide-react'

// أيقونة واتساب الرسمية بمسار SVG دقيق
const WhatsAppIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.99c-.002 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
  </svg>
)

const WhatsAppFloat: React.FC = () => {
  const { t, isRTL } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const handleWhatsAppClick = () => {
    const phoneNumber = '966502035455'
    const message = encodeURIComponent(t('whatsapp.message') || 'السلام عليكم، أود الاستفسار عن خدماتكم')
    const isMobile = window.innerWidth < 768
    const whatsappUrl = isMobile
      ? `https://wa.me/${phoneNumber}?text=${message}`
      : `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${message}`
    
    window.open(whatsappUrl, '_blank')
    setIsOpen(false)
  }

  return (
    // تم تعديل الإحداثيات هنا لتكون متناسقة تماماً ومحاذاة على خط مستقيم
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
      <div className="relative group flex items-center justify-center">
        
        {/* حلقة التنبيه النابضة */}
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-30 animate-ping" />

        {/* الزر الرئيسي للواتساب */}
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="relative flex items-center justify-center p-3.5 md:p-4 rounded-full text-white shadow-xl transition-transform cursor-pointer"
          style={{ backgroundColor: '#25D366' }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label="WhatsApp Contact"
        >
          {isOpen ? (
            <X className="w-6 h-6 stroke-[2.5]" />
          ) : (
            <WhatsAppIcon className="w-6 h-6 md:w-7 md:h-7" />
          )}
        </motion.button>

        {/* التلميح التوضيحي: يظهر على اليسار دائماً عند Hover */}
        {!isOpen && (
          <div 
            className="hidden md:block absolute top-1/2 -translate-y-1/2 right-16 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
            style={{
              backgroundColor: 'var(--surface)',
              color: 'var(--text)',
              border: '1px solid var(--border)'
            }}
          >
            {t('whatsapp.title') || 'تواصل معنا'}
          </div>
        )}
      </div>

      {/* نافذة المحادثة المنبثقة */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="absolute bottom-20 right-0 w-80 max-w-[calc(100vw-2.5rem)] md:w-88 rounded-2xl border shadow-2xl overflow-hidden z-50"
            style={{
              backgroundColor: 'var(--surface)',
              borderColor: 'var(--border)'
            }}
            initial={{ opacity: 0, y: 15, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 15, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {/* Header النافذة */}
            <div 
              className="p-4 flex items-center justify-between border-b"
              style={{
                backgroundColor: 'var(--surface-2)',
                borderColor: 'var(--border)'
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md"
                  style={{ backgroundColor: '#25D366' }}
                >
                  <WhatsAppIcon className="w-5 h-5" />
                </div>
                <div>
                  <h4 
                    className="font-bold text-sm leading-tight"
                    style={{ color: 'var(--text)' }}
                  >
                    {t('whatsapp.title') || 'الدعم الفني والخدمات'}
                  </h4>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
                    <span className="text-xs font-medium" style={{ color: 'var(--cyan)' }}>
                      {t('whatsapp.online') || 'متصل الآن'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg transition-colors cursor-pointer"
                style={{ color: 'var(--text-muted)' }}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* جسم النافذة */}
            <div className="p-4 space-y-4">
              <div 
                className="p-3.5 rounded-xl text-xs leading-relaxed border relative"
                style={{
                  backgroundColor: 'var(--surface-soft)',
                  borderColor: 'var(--border)',
                  color: 'var(--text-2)'
                }}
              >
                {t('whatsapp.message') || 'مرحباً بك! كيف يمكننا مساعدتك اليوم؟'}
              </div>

              {/* زر إرسال المحادثة */}
              <button
                onClick={handleWhatsAppClick}
                className="w-full py-3 px-4 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 shadow-md transition-all duration-200 cursor-pointer"
                style={{ backgroundColor: '#026325' }}
                onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.95)'}
                onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
              >
                <WhatsAppIcon className="w-4 h-4" />
                <span>{t('whatsapp.button') || 'بدء المحادثة'}</span>
              </button>

              {/* تفاصيل الرقم وساعات العمل */}
              <div 
                className="pt-3 border-t flex items-center justify-between text-[11px]"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
              >
                <div className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  <span dir="ltr">+966 50 203 5455</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" style={{ color: 'var(--gold)' }} />
                  <span>24/7 متواجدون</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default WhatsAppFloat